import {
	TRAVEL_DAYS_OF_THE_WEEK,
	TRAVEL_EXPENSE_CODE_TYPE,
	TRAVEL_LOCATION_CATEGORIES,
	TRAVEL_LOCATION_TYPE,
	TRAVEL_RIDE_SERVICE,
	TRAVEL_RIDE_TYPE,
	TRAVEL_TIME_TYPE,
} from '@/constants/travel-program';
import { VALIDATION_MESSAGES } from '@/constants/validation';
import type { LocationAllowed, TravelProgramFormData } from '@/types';
import type { MixedSchema, NumberSchema, StringSchema } from 'yup';
import { array, bool, mixed, number, object, string } from 'yup';

export const travelProgramSchema = object({
	programName: string().trim().required(VALIDATION_MESSAGES.required),
	programDescription: string().trim(),
	expenseCodeType: string()
		.oneOf(Object.values(TRAVEL_EXPENSE_CODE_TYPE))
		.required(VALIDATION_MESSAGES.required),
	expenseCodes: mixed<File>()
		.test(
			'fileFormat',
			'Unsupported file format',
			(value) => !value || value.type.includes('text/csv'),
		)
		.when('expenseCodeType', {
			is: 'required',
			// biome-ignore lint/suspicious/noThenProperty: `then` is part of the schema
			then: (schema: MixedSchema) =>
				schema.required(VALIDATION_MESSAGES.file_required),
		}),

	locationType: string()
		.oneOf(Object.values(TRAVEL_LOCATION_TYPE))
		.required(VALIDATION_MESSAGES.required),
	locationAllowed: mixed<LocationAllowed>()
		.oneOf(TRAVEL_LOCATION_CATEGORIES.map((category) => category.value))
		.when('locationType', {
			is: TRAVEL_LOCATION_TYPE.limited,
			// biome-ignore lint/suspicious/noThenProperty: `then` is part of schema
			then: (schema: MixedSchema) =>
				schema.required(VALIDATION_MESSAGES.required),
		}),
	locationName: string().when('locationType', {
		is: TRAVEL_LOCATION_TYPE.limited,
		// biome-ignore lint/suspicious/noThenProperty: `then` is part of schema
		then: (schema: StringSchema) =>
			schema.required(VALIDATION_MESSAGES.required),
	}),
	locationAddress: string().when('locationType', {
		is: TRAVEL_LOCATION_TYPE.limited,
		// biome-ignore lint/suspicious/noThenProperty: `then` is part of schema
		then: (schema: StringSchema) =>
			schema.required(VALIDATION_MESSAGES.required),
	}),
	locationRadius: number().when('locationType', {
		is: TRAVEL_LOCATION_TYPE.limited,
		// biome-ignore lint/suspicious/noThenProperty: `then` is part of schema
		then: (schema: NumberSchema) =>
			schema.required(VALIDATION_MESSAGES.required),
	}),
	timeType: string()
		.oneOf(Object.values(TRAVEL_TIME_TYPE))
		.required(VALIDATION_MESSAGES.required),
	isAllDay: bool().oneOf([true, false]),
	startTime: string().when('timeType', {
		is: TRAVEL_TIME_TYPE.custom,
		// biome-ignore lint/suspicious/noThenProperty: `then` is part of the schema
		then: (schema: StringSchema) =>
			schema.when('isAllDay', {
				is: true,
				// biome-ignore lint/suspicious/noThenProperty: `then` is part of the schema
				then: (schema: StringSchema) => schema.nullable(),
				otherwise: (schema: StringSchema) =>
					schema.required(VALIDATION_MESSAGES.required),
			}),
	}),
	endTime: string().when('timeType', {
		is: TRAVEL_TIME_TYPE.custom,
		// biome-ignore lint/suspicious/noThenProperty: `then` is part of the schema
		then: (schema: StringSchema) =>
			schema.when('isAllDay', {
				is: true,
				// biome-ignore lint/suspicious/noThenProperty: `then` is part of the schema
				then: (schema: StringSchema) => schema.nullable(),
				otherwise: (schema: StringSchema) =>
					schema.required(VALIDATION_MESSAGES.required),
			}),
	}),
	days: array()
		.of(string().oneOf(TRAVEL_DAYS_OF_THE_WEEK.map((day) => day.value)))
		.min(1, VALIDATION_MESSAGES.at_least_one)
		.when('timeType', {
			is: TRAVEL_TIME_TYPE.custom,
			// biome-ignore lint/suspicious/noThenProperty: `then` is part of the schema
			then: (schema) => schema.required(VALIDATION_MESSAGES.required),
		}),
	rideType: string()
		.oneOf(Object.values(TRAVEL_RIDE_TYPE))
		.required(VALIDATION_MESSAGES.required),
	rides: array().when('rideType', {
		is: TRAVEL_RIDE_TYPE.limited,
		// biome-ignore lint/suspicious/noThenProperty: <explanation>
		then: (schema) =>
			schema
				.of(string().oneOf(Object.values(TRAVEL_RIDE_SERVICE)))
				.min(1, VALIDATION_MESSAGES.at_least_one),
	}),
});

export const travelProgramInitialValues: TravelProgramFormData = {
	programName: '',
	programDescription: '',
	expenseCodeType: 'none',
	expenseCodes: undefined,
	locationType: 'any',
	locationAllowed: undefined,
	locationName: undefined,
	locationAddress: undefined,
	locationRadius: undefined,
	timeType: 'all-day',
	isAllDay: false,
	startTime: '',
	endTime: '',
	days: [],
	rideType: 'all',
	rides: [],
};
